import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  VStack,
  Text,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
} from "@chakra-ui/react";
import { Formik, Form, Field, FieldProps } from "formik";
import { CherryPayApi } from "../../api/models";
import { useApiClient } from "../../hooks/useApiClient";

interface TransferFundsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  memberId: string;
  sourceCard: CherryPayApi.CherryPayCardCompact;
  currentBalance: number;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface TransferFormValues {
  targetCardId: string;
  amount: string;
  description: string;
}

interface FormErrors {
  targetCardId?: string;
  amount?: string;
  description?: string;
}

const validateForm = (
  values: TransferFormValues,
  maxBalance: number
): FormErrors => {
  const errors: FormErrors = {};

  if (!values.targetCardId) {
    errors.targetCardId = "Please select a target card";
  }

  if (!values.amount) {
    errors.amount = "Amount is required";
  } else {
    const amount = parseFloat(values.amount);
    if (isNaN(amount) || amount <= 0) {
      errors.amount = "Amount must be greater than $0.00";
    } else if (amount > maxBalance) {
      errors.amount = "Amount cannot exceed current balance";
    }
  }

  if (!values.description || values.description.trim().length === 0) {
    errors.description = "Description is required";
  }

  return errors;
};

export const TransferFundsDialog = ({
  isOpen,
  onClose,
  businessId,
  memberId,
  sourceCard,
  currentBalance,
  onSuccess,
  onError,
}: TransferFundsDialogProps) => {
  const apiClient = useApiClient();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const [memberCards, setMemberCards] = useState<
    CherryPayApi.CherryPayCardCompact[]
  >([]);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchMemberCards = async () => {
      if (!isOpen || !memberId) return;

      setIsLoadingCards(true);
      const result = await apiClient.getMemberCardList(businessId, memberId);

      if (result.ok) {
        const filteredCards = result.data.filter(
          (card) =>
            card.CardId !== sourceCard.CardId &&
            card.MemberId === sourceCard.MemberId &&
            card.MobileNumber === sourceCard.MobileNumber &&
            card.CardProgramType?.toLowerCase() === "reloadable"
        );
        setMemberCards(filteredCards);
      }
      setIsLoadingCards(false);
    };

    fetchMemberCards();
  }, [
    isOpen,
    memberId,
    businessId,
    sourceCard.CardId,
    sourceCard.MemberId,
    sourceCard.MobileNumber,
    apiClient,
  ]);

  const handleSubmit = async (values: TransferFormValues) => {
    setIsSubmitting(true);

    const result = await apiClient.transferCardFunds(
      businessId,
      memberId,
      sourceCard.CardId ?? "",
      values.targetCardId,
      parseFloat(values.amount),
      values.description
    );

    if (result.ok) {
      const message = `Successfully transferred $${parseFloat(
        values.amount
      ).toFixed(2)} to card ${
        memberCards.find((c) => c.CardId === values.targetCardId)
          ?.ExternalAccountId
      }`;
      onSuccess(message);
      onClose();
    } else {
      onError(result.message ?? "Failed to transfer funds");
    }

    setIsSubmitting(false);
  };

  const initialValues: TransferFormValues = {
    targetCardId: "",
    amount: "",
    description: "",
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      initialFocusRef={cancelRef}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Transfer Funds</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialValues}
          validate={(values) => validateForm(values, currentBalance)}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, submitForm }) => (
            <Form>
              <ModalBody pb={6}>
                <Box mb={4}>
                  <Text fontWeight="bold">Source Card:</Text>
                  <Text>{sourceCard.ExternalAccountId}</Text>
                  <Text fontSize="sm" color="gray.600">
                    Current Balance: ${currentBalance.toFixed(2)}
                  </Text>
                </Box>

                {isLoadingCards ? (
                  <Text>Loading available cards...</Text>
                ) : memberCards.length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    <AlertDescription>
                      No other cards available for this member to transfer funds
                      to.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <Field name="targetCardId">
                      {({ field, form }: FieldProps) => (
                        <FormControl
                          isInvalid={
                            !!(
                              form.errors.targetCardId &&
                              form.touched.targetCardId
                            )
                          }
                          isRequired
                        >
                          <FormLabel>Target Card</FormLabel>
                          <Select
                            {...field}
                            placeholder="Select a card"
                            isDisabled={isSubmitting}
                          >
                            {memberCards.map((card) => (
                              <option key={card.CardId} value={card.CardId}>
                                {card.ExternalAccountId}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>
                            {form.errors.targetCardId as string}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="amount">
                      {({ field, form }: FieldProps) => (
                        <FormControl
                          isInvalid={
                            !!(form.errors.amount && form.touched.amount)
                          }
                          isRequired
                        >
                          <FormLabel>Amount</FormLabel>
                          <NumberInput
                            min={0.01}
                            max={currentBalance}
                            precision={2}
                            step={0.01}
                            isDisabled={isSubmitting}
                            value={field.value}
                            onChange={(valueString) =>
                              form.setFieldValue("amount", valueString)
                            }
                          >
                            <NumberInputField {...field} placeholder="0.00" />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <Text fontSize="xs" color="gray.600" mt={1}>
                            Maximum: ${currentBalance.toFixed(2)}
                          </Text>
                          <FormErrorMessage>
                            {form.errors.amount as string}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>

                    <Field name="description">
                      {({ field, form }: FieldProps) => (
                        <FormControl
                          isInvalid={
                            !!(
                              form.errors.description &&
                              form.touched.description
                            )
                          }
                          isRequired
                        >
                          <FormLabel>Description</FormLabel>
                          <Textarea
                            {...field}
                            placeholder="Enter a description for this transfer"
                            maxLength={64}
                            isDisabled={isSubmitting}
                          />
                          <FormErrorMessage>
                            {form.errors.description as string}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </VStack>
                )}
              </ModalBody>

              <ModalFooter>
                <Button
                  ref={cancelRef}
                  onClick={onClose}
                  mr={3}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  ref={submitRef}
                  colorScheme="cherryButton"
                  onClick={submitForm}
                  isLoading={isSubmitting}
                  isDisabled={isLoadingCards || memberCards.length === 0}
                >
                  Transfer Funds
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
};
